import Component from "@glimmer/component";
import { service } from "@ember/service";
import { on } from "@ember/modifier";
import { task } from "ember-concurrency";
import { eq } from "ember-truth-helpers";
import { t } from "ember-intl";
import ENV from "glesys-controlpanel/config/environment";
import Box from "glesys-controlpanel/components/box";
import Table from "glesys-controlpanel/components/table";
import TableTd from "glesys-controlpanel/components/table/td";
import TableTh from "glesys-controlpanel/components/table/th";
import Copyable from "glesys-controlpanel/components/copyable";
import GButton from "glesys-controlpanel/components/g-button";
import Dropdown from "glesys-controlpanel/components/dropdown";
import DropdownItem from "glesys-controlpanel/components/dropdown/item";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import IconsMoreVertical from "glesys-controlpanel/components/icons/more-vertical";
import canManage from "glesys-controlpanel/helpers/can-manage";
import modal from "glesys-controlpanel/helpers/modal";

export default class UsersBox extends Component {
  @service http;
  @service store;

  get instanceUsers() {
    return this.args.database.instanceUsers ?? [];
  }

  get instanceDatabases() {
    return this.args.database.instanceDatabases ?? [];
  }

  createUserTask = task(async (username) => {
    let response = await this.http.request({
      method: "POST",
      url: `${ENV.apiBaseUri}/database/createuser`,
      body: { instanceid: this.args.database.id, username },
    });

    let { user, database } = response.content;

    this.store.pushPayload("database", { database });

    return user;
  });

  resetPasswordTask = task(async (user) => {
    let response = await this.http.request({
      method: "POST",
      url: `${ENV.apiBaseUri}/database/resetuserpassword`,
      body: { userid: user.id },
    });

    return response.content.user;
  });

  setPermissionsTask = task(async (user, permissions) => {
    let response = await this.http.request({
      method: "POST",
      url: `${ENV.apiBaseUri}/database/setuserpermissions`,
      body: { userid: user.id, permissions },
    });

    this.store.pushPayload("database", { database: response.content.database });
  });

  <template>
    <Box data-test-database-details-users @showFooter={{(canManage)}}>
      <:header>{{t "databases.details.users.title"}}</:header>
      <:default>
        <Table class="mx-[-25px]" @items={{this.instanceUsers}} @footer={{false}} @outsideBorders={{false}}>
          <:header>
            <TableTh class="w-48">{{t "databases.details.users.columns.username"}}</TableTh>
            <TableTh>{{t "databases.details.users.columns.permissions"}}</TableTh>
            <TableTh><span class="sr-only">{{t "label.actions"}}</span></TableTh>
          </:header>
          <:body as |user|>
            <TableTd>
              <Copyable>{{user.username}}</Copyable>
            </TableTd>
            <TableTd>
              <div class="flex flex-wrap gap-2">
                {{#each user.permissions as |permission|}}
                  <span
                    class="inline-flex items-baseline gap-1.5 text-sm px-2.5 py-1 rounded-full bg-gray-100"
                    data-test-permission
                  >
                    <span class="text-gray-900">{{permission.name}}</span>
                    {{! eslint-disable ember/template-no-bare-strings }}
                    <span class="text-gray-400">·</span>
                    {{! eslint-enable ember/template-no-bare-strings }}
                    <span class="text-gray-600">
                      {{#if (eq permission.permission "WRITE")}}
                        {{t "databases.details.users.permission.write"}}
                      {{else}}
                        {{t "databases.details.users.permission.read"}}
                      {{/if}}
                    </span>
                  </span>
                {{else}}
                  <span class="text-gray-500">{{t "databases.details.users.no-access"}}</span>
                {{/each}}
              </div>
            </TableTd>
            <TableTd>
              {{#if (canManage)}}
                <div class="flex justify-end">
                  <Dropdown
                    aria-label={{t "label.action-menu-aria-label" name=user.username}}
                    data-test-database-user-menu={{user.username}}
                    @chevron={{false}}
                  >
                    <:header>
                      <IconsMoreVertical class="w-5" />
                    </:header>
                    <:default>
                      <DropdownItem
                        data-test-database-user-reset-password
                        {{on
                          "click"
                          (modal "modals/database-reset-user-password" user=user task=this.resetPasswordTask)
                        }}
                      >
                        {{t "databases.details.users.reset.button"}}
                      </DropdownItem>
                      {{#unless (eq user.username "dbadmin")}}
                        <DropdownItem
                          data-test-database-user-permissions
                          {{on
                            "click"
                            (modal
                              "modals/database-edit-user-permissions"
                              user=user
                              databases=this.instanceDatabases
                              task=this.setPermissionsTask
                            )
                          }}
                        >
                          {{t "databases.details.users.edit-permissions.button"}}
                        </DropdownItem>
                        <DropdownDestructiveItem
                          data-test-database-user-delete
                          {{on
                            "click"
                            (modal
                              "modals/organism-delete"
                              entity=user.username
                              entityKey=user.username
                              task=user.deleteTask
                              leadText=(t "databases.details.users.delete.warning")
                            )
                          }}
                        >
                          {{t "button.delete"}}
                        </DropdownDestructiveItem>
                      {{/unless}}
                    </:default>
                  </Dropdown>
                </div>
              {{/if}}
            </TableTd>
          </:body>
        </Table>
      </:default>
      <:footer>
        <GButton
          data-test-database-users-create
          @color="outline"
          {{on "click" (modal "modals/database-create-user" task=this.createUserTask)}}
        >
          {{t "databases.details.users.create.button"}}
        </GButton>
      </:footer>
    </Box>
  </template>
}
